import React, { useState } from 'react';
import { segmentCase, useIsMobile } from '@guildeducationinc/catalog-tools';
import { Button, Typography } from '@guildeducationinc/recess';
import { GetMemberProgramQuery } from 'src/graphqlTypes/catalogServiceTypes';
import { GetRolesForProgramsQuery } from 'src/graphqlTypes/roleProgramGraphServiceTypes';
import { Skeleton } from 'src/components/UserFacetSkeletonLoader';
import { trackEvent } from '@src/services/analytics';
import ExpandMoreIcon from '@guildeducationinc/recess/icons/ExpandMoreIcon';
import classNames from 'classnames';
import { Pathway } from './Pathway';
import styles from './CareerPathways.module.scss';

interface Props {
	loading: boolean;
	program: GetMemberProgramQuery['memberProgram'];
	rolesForPrograms: GetRolesForProgramsQuery['rolesForPrograms'];
}

export interface PathwayWithRoles {
	pathwayId: string;
	pathwayName: string;
	roles: {
		id: string;
		name: string;
		genericName: string;
	}[];
}

export const ROLES_DISCLAIMER =
	"We can't promise that you'll earn any specific role in these or other fields upon completing this program. But our programs do offer high-quality training in skills related to these fields. Connect with your employer about how this program might enhance your job or career.";

export const CareerPathways: React.FC<Props> = ({ loading, program, rolesForPrograms }) => {
	const isMobile = useIsMobile();
	const [showMore, setShowMore] = useState(false);

	const pathwaysMap: { [key: string]: PathwayWithRoles } = {};

	rolesForPrograms[0].roles.forEach(role => {
		const { pathwayId, pathwayName } = role;
		if (!pathwaysMap[pathwayId]) {
			pathwaysMap[pathwayId] = {
				pathwayId,
				pathwayName,
				roles: [],
			};
		}
		pathwaysMap[pathwayId].roles.push({
			id: role.id,
			name: role.name,
			genericName: role.genericName,
		});
	});

	const pathways = Object.values(pathwaysMap);

	const handleClick = () => {
		const buttonText = showMore ? 'show_less' : 'show_more';
		trackEvent('Clickable', {
			tag: `career_pathways_${buttonText}`,
			text: `${segmentCase(buttonText)}`,
			type: 'button',
		});
		setShowMore(!showMore);
	};

	const hasFewerThanTwoPathways = pathways.length <= 2;

	return (
		<section
			aria-label='career pathways'
			className={styles.sectionContainer}
			data-testid='career-pathways'
			data-title='Career Pathways'
			id='detail-career-pathways'
			tabIndex={-1}
		>
			<Skeleton count={3} dataLoading={loading} height={64}>
				<Typography className={styles.header} variant='h2'>
					Career Pathways
				</Typography>
				<Typography
					className={styles.subhead}
					testid='concentrations-subhead'
					variant={isMobile ? 'body1' : 'body2'}
				>
					Apply the skills you learn in {program?.studentFacingAcademicPartner?.name}&apos;s{' '}
					{program?.name} to roles in these career pathways.
				</Typography>
				<div className={showMore || hasFewerThanTwoPathways ? null : styles.pathwaysFade}>
					{pathways
						.map(pathway => {
							return <Pathway key={pathway.pathwayId} pathway={pathway} />;
						})
						.filter((_, index) => (showMore ? true : index < 2))}
				</div>
				{(showMore || (!showMore && hasFewerThanTwoPathways)) && (
					<Typography className={styles.disclaimer} variant='body1'>
						{ROLES_DISCLAIMER}
					</Typography>
				)}
				{!hasFewerThanTwoPathways && (
					<Button
						appearance='ghost'
						aria-expanded={showMore}
						className={styles.showMoreBtn}
						icon={
							<ExpandMoreIcon
								className={classNames({ [styles.showMoreIcon]: showMore })}
								size={24}
							/>
						}
						iconDirection='right'
						onClick={handleClick}
						testid='show-more-button'
					>
						{showMore ? 'Show less' : 'Show more'}
					</Button>
				)}
			</Skeleton>
		</section>
	);
};
